import React, { useState, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { App as AntApp, ConfigProvider } from 'antd';
import viVN from 'antd/locale/vi_VN';
import './App.css';

// Layouts
import AdminLayout from './layouts/AdminLayout';

// Pages
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import ProductCreate from './pages/ProductCreate';
import ProductEdit from './pages/ProductEdit';
import Categories from './pages/Categories';
import CategoryCreate from './pages/CategoryCreate';
import CategoryEdit from './pages/CategoryEdit';
import Orders from './pages/Orders';
import Promotions from './pages/Promotions';
import PromotionCreate from './pages/PromotionCreate';
import Users from './pages/Users';
import UserCreate from './pages/UserCreate';
import UserEdit from './pages/UserEdit';
import Settings from './pages/Settings';
import Login from './pages/Login';
import Collections from './pages/Collections'
import Branches from './pages/Branches'
import Customers from './pages/Customers'
import Logs from './pages/Logs'
import Sections from './pages/Sections'
import Articles from './pages/Articles'
import ArticleCreate from './pages/ArticleCreate'
import ArticleEdit from './pages/ArticleEdit'

// Components
import ProtectedRoute from './components/auth/ProtectedRoute';
import RoleProtectedRoute from './components/auth/RoleProtectedRoute';
import DefaultRouteRedirect from './components/auth/DefaultRouteRedirect';

// Services
import authService from './services/authService';

// Context
import { AuthProvider } from './context/AuthContext';

import { routeConfigs } from './utils/constant';

// Ant Design theme configuration
const theme = {
  token: {
    colorPrimary: '#10b981',
    borderRadius: 4,
  },
  components: {
    Notification: {
      colorSuccess: '#10b981',
      colorError: '#ef4444',
      colorWarning: '#f97316',
      colorInfo: '#3b82f6',
    },
  },
};

// Cấu hình routes
const adminRoutes = [
  {
    path: 'dashboard',
    element: <Dashboard />,
    roles: routeConfigs.dashboard.roles
  },
  {
    path: 'orders',
    element: <Orders />,
    roles: routeConfigs.orders.roles
  },
  {
    path: 'products',
    element: <Products />,
    roles: routeConfigs.products.roles
  },
  {
    path: 'products/new',
    element: <ProductCreate />,
    roles: routeConfigs.products.roles
  },
  {
    path: 'products/:id',
    element: <ProductEdit />,
    roles: routeConfigs.products.roles
  },
  {
    path: 'collections',
    element: <Collections />,
    roles: routeConfigs.collections.roles
  },
  {
    path: 'categories',
    element: <Categories />,
    roles: routeConfigs.categories.roles
  },
  {
    path: 'categories/new',
    element: <CategoryCreate />,
    roles: routeConfigs.categories.roles
  },
  {
    path: 'categories/:id',
    element: <CategoryEdit />,
    roles: routeConfigs.categories.roles
  },
  {
    path: 'promotions',
    element: <Promotions />,
    roles: routeConfigs.promotions.roles
  },
  {
    path: 'promotions/new',
    element: <PromotionCreate />,
    roles: routeConfigs.promotions.roles
  },
  {
    path: 'sections',
    element: <Sections />,
    roles: routeConfigs.sections.roles
  },
  {
    path: 'branches',
    element: <Branches />,
    roles: routeConfigs.branches.roles
  },
  {
    path: 'customers',
    element: <Customers />,
    roles: routeConfigs.customers.roles
  },
  {
    path: 'users',
    element: <Users />,
    roles: routeConfigs.users.roles
  },
  {
    path: 'users/new',
    element: <UserCreate />,
    roles: routeConfigs.users.roles
  },
  {
    path: 'users/:id',
    element: <UserEdit />,
    roles: routeConfigs.users.roles
  },
  {
    path: 'settings',
    element: <Settings />,
    roles: routeConfigs.settings.roles
  },
  {
    path: 'logs',
    element: <Logs />,
    roles: routeConfigs.logs.roles
  },
  {
    path: 'articles',
    element: <Articles />,
    roles: routeConfigs.articles.roles
  },
  {
    path: 'articles/new',
    element: <ArticleCreate />,
    roles: routeConfigs.articles.roles
  },
  {
    path: 'articles/:id',
    element: <ArticleEdit />,
    roles: routeConfigs.articles.roles
  }
]

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const checkAuthStatus = () => {
      const authStatus = authService.isAuthenticated();
      setIsAuthenticated(authStatus);
      setIsLoading(false);
    };

    checkAuthStatus();
  }, []);

  const handleLogin = async (
    email: string,
    password: string,
    remember: boolean = true
  ): Promise<boolean> => {
    try {
      setIsLoading(true);
      const response = await authService.login(email, password, remember);

      if (response && response.user) {
        localStorage.setItem('user', JSON.stringify(response.user));
      }

      const currentPath = window.location.pathname;
      if (currentPath !== '/login') {
        localStorage.setItem('lastPath', currentPath);
      }

      setIsAuthenticated(true);
      return true;
    } catch (error: any) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      setIsLoading(true);
      await authService.logout();
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ConfigProvider 
      locale={viVN} 
      theme={theme}
    >
      <AntApp>
        <AuthProvider>
          <Router>
            <Routes>
              {/* Route mặc định */}
              <Route
                path='/'
                element={
                  isAuthenticated ? (
                    <DefaultRouteRedirect />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />

              <Route
                path='/login'
                element={
                  isAuthenticated ? (
                    <DefaultRouteRedirect />
                  ) : (
                    <Login onLogin={handleLogin} />
                  )
                }
              />

              <Route
                path='/*'
                element={
                  <Routes>
                    <Route
                      path='admin'
                      element={
                        <ProtectedRoute isAuthenticated={isAuthenticated}>
                          <AdminLayout onLogout={handleLogout} />
                        </ProtectedRoute>
                      }
                    >
                      {adminRoutes.map(({ path, element, roles }) => (
                        <Route
                          key={path}
                          path={path}
                          element={
                            <RoleProtectedRoute allowedRoles={roles}>
                              {element}
                            </RoleProtectedRoute>
                          }
                        />
                      ))}
                    </Route>
                  </Routes>
                }
              />
            </Routes>
          </Router>
        </AuthProvider>
      </AntApp>
    </ConfigProvider>
  );
};

export default App;
